import { formatMoney, formatDateTime } from '@/lib/format';
import { CASH_SESSION_STATUS_LABELS, differenceLabel, formatPeriodLabel } from '@/lib/cashSession';
import type { Branch, CashSession } from '@/lib/types';
import type { PaperWidth } from './printTypes';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function cashCss(paperWidth?: PaperWidth | string): string {
  const width = paperWidth === '58mm' ? '58mm' : '80mm';
  const font = width === '58mm' ? '11.5px' : '13px';
  return `
  @page { size: ${width} auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: ${width}; background: #fff; }
  body { font-family: 'Courier New', Courier, monospace; color: #000; }
  .receipt { width: 100%; padding: 4mm; font-size: ${font}; line-height: 1.6; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .divider { border-top: 1px dashed #000; margin: 3.5mm 0; }
  .row { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; margin: 1.4mm 0; }
  .row > span:first-child { flex: 1 1 auto; word-break: break-word; }
  .row > span:last-child { flex: 0 0 auto; text-align: right; }
  .small { font-size: ${width === '58mm' ? '10.5px' : '12px'}; }
`;
}

export interface CashPrintData {
  branch: Branch | null;
  session: CashSession;
  currencySymbol?: string;
  paperWidth?: PaperWidth | string;
}

export function buildCashSessionHTML(data: CashPrintData): string {
  const { branch, session, currencySymbol = 'RD$' } = data;
  const hasCount = session.status === 'closed' && session.cash_counted != null;
  const diff = hasCount ? Number(session.difference ?? 0) : 0;
  const diffMeta = differenceLabel(diff);

  const breakdownRows = (session.payment_breakdown ?? [])
    .filter((r) => r.count > 0)
    .map(
      (r) =>
        `<div class="row"><span>${escapeHtml(r.label)} (${r.count})</span><span>${formatMoney(
          Number(r.total),
          currencySymbol
        )}</span></div>`
    )
    .join('');

  return `<!doctype html><html><head><meta charset="utf-8"><style>${cashCss(
    data.paperWidth
  )}</style></head><body>
    <div class="receipt">
      <div class="center bold">${escapeHtml(branch?.name || 'Mi Tienda')}</div>
      ${branch?.legal_name ? `<div class="center small">${escapeHtml(branch.legal_name)}</div>` : ''}
      ${branch?.rnc ? `<div class="center small">RNC: ${escapeHtml(branch.rnc)}</div>` : ''}
      <div class="divider"></div>
      <div class="center bold">CUADRE DE CAJA</div>
      <div class="divider"></div>
      <div class="row"><span>Responsable:</span><span>${escapeHtml(session.user_name || '—')}</span></div>
      <div class="row"><span>Rol:</span><span>${escapeHtml(session.user_role || '—')}</span></div>
      <div class="row"><span>Sucursal:</span><span>${escapeHtml(branch?.name || '—')}</span></div>
      <div class="row"><span>Fecha:</span><span>${formatPeriodLabel(session.period_date)}</span></div>
      <div class="row"><span>Ventas:</span><span>${Number(session.sales_count || 0)}</span></div>
      <div class="divider"></div>
      ${breakdownRows}
      <div class="divider"></div>
      <div class="row"><span>Subtotal</span><span>${formatMoney(Number(session.subtotal), currencySymbol)}</span></div>
      <div class="row"><span>Descuentos</span><span>-${formatMoney(Number(session.discount), currencySymbol)}</span></div>
      <div class="row"><span>Impuestos</span><span>${formatMoney(Number(session.tax), currencySymbol)}</span></div>
      ${Number(session.returns_total) > 0 ? `<div class="row"><span>Devoluciones</span><span>-${formatMoney(Number(session.returns_total), currencySymbol)}</span></div>` : ''}
      <div class="row bold"><span>TOTAL VENDIDO</span><span>${formatMoney(Number(session.total), currencySymbol)}</span></div>
      <div class="divider"></div>
      <div class="row"><span>Costo de mercancia</span><span>${formatMoney(Number(session.cost_total || 0), currencySymbol)}</span></div>
      <div class="row bold"><span>GANANCIA</span><span>${formatMoney(Number(session.profit || 0), currencySymbol)}</span></div>
      <div class="row"><span>Margen</span><span>${Number(session.margin || 0).toFixed(1)}%</span></div>
      <div class="divider"></div>
      <div class="row"><span>Efectivo esperado</span><span>${formatMoney(Number(session.cash_expected), currencySymbol)}</span></div>
      ${hasCount ? `<div class="row"><span>Efectivo contado</span><span>${formatMoney(Number(session.cash_counted), currencySymbol)}</span></div>` : ''}
      ${hasCount ? `<div class="row bold"><span>DIFERENCIA</span><span>${formatMoney(diff, currencySymbol)}</span></div>` : ''}
      ${hasCount && diffMeta.kind !== 'ok' ? `<div class="center bold">${diffMeta.title}: ${formatMoney(Math.abs(diff), currencySymbol)}</div>` : ''}
      ${hasCount && diffMeta.kind === 'ok' ? `<div class="center bold">CUADRE CORRECTO</div>` : ''}
      <div class="divider"></div>
      <div class="row"><span>Estado:</span><span class="bold">${escapeHtml(CASH_SESSION_STATUS_LABELS[session.status])}</span></div>
      ${session.closed_at ? `<div class="row"><span>Cierre:</span><span>${formatDateTime(session.closed_at)}</span></div>` : ''}
      ${session.observations ? `<div class="divider"></div><div class="small">Observación: ${escapeHtml(session.observations)}</div>` : ''}
      <div class="divider"></div>
      <div class="center small">Firma: _______________________</div>
    </div>
  </body></html>`;
}